import React from "react";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function RecentApplicationsWidget() {
    const navigate = useNavigate();

    const { data: analytics } = useQuery({
        queryKey: ["dashboard", "analytics"],
        queryFn: () => backendApi.entities.Dashboard.analytics(),
        staleTime: 60000,
        enabled: !!backendApi.auth.getToken(),
    });

    const applications = analytics?.recentActivity || [];

    const getStatusStyles = (status) => {
        const s = status?.toLowerCase() || 'pending';
        if (s === 'rejected') return { color: "text-red-600", bg: "bg-red-100", dot: "bg-red-500", label: "Rejected" };
        if (s === 'reviewed' || s === 'shortlisted') return { color: "text-green-600", bg: "bg-green-100", dot: "bg-green-500", label: "Shortlisted" };
        if (s === 'accepted') return { color: "text-blue-600", bg: "bg-blue-100", dot: "bg-blue-500", label: "Accepted" };
        return { color: "text-yellow-600", bg: "bg-yellow-100", dot: "bg-yellow-500", label: "Under Review" };
    };

    if (!applications || applications.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-gray-900 font-semibold">Recent Applications</h3>
                    <button
                        onClick={() => navigate('/jobs/applications')}
                        className="text-[#2557a7] text-sm font-medium hover:underline"
                    >
                        View all &gt;
                    </button>
                </div>
                <div className="text-gray-500 italic text-center py-6 border border-dashed rounded-lg bg-gray-50 border-gray-200">
                    You haven't applied to any jobs yet. Check out our recommended jobs!
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-gray-900 font-semibold">Recent Applications</h3>
                <button
                    onClick={() => navigate('/jobs/applications')}
                    className="text-[#2557a7] text-sm font-medium hover:underline"
                >
                    View all &gt;
                </button>
            </div>

            <div className="space-y-0">
                {applications.map((app, index) => {
                    const styles = getStatusStyles(app.status);
                    return (
                        <div
                            key={index}
                            className={`flex items-center justify-between py-4 ${index !== applications.length - 1 ? "border-b border-gray-50 mb-0" : ""
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded bg-gray-50 flex items-center justify-center border border-gray-100 shrink-0">
                                    <Building2 className="w-5 h-5 text-gray-400" />
                                </div>
                                <div className="overflow-hidden">
                                    <h4 className="font-medium text-gray-900 text-sm leading-tight mb-0.5 truncate">
                                        {app.title}
                                    </h4>
                                    <p className="text-xs text-gray-500 truncate">{app.company_name}</p>
                                </div>
                            </div>

                            <div className="flex flex-col items-end gap-1.5 shrink-0 pl-2">
                                <span
                                    className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${styles.bg} ${styles.color}`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`}></span>
                                    {styles.label}
                                </span>
                                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                    📅 {new Date(app.updated_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <button
                onClick={() => navigate('/jobs/applications')}
                className="w-full mt-4 py-2 border border-[#2557a7] text-[#2557a7] rounded-lg text-sm font-medium hover:bg-blue-50 transition"
            >
                View all applications
            </button>
        </div>
    );
}
